import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { AuditApiService } from '../services/audit-api.service';
import { AuditLogResponse, AuditLogSearchParams } from '../models/audit.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../shared/services/user-directory-api.service';
import { AuditDisplayService, AuditDiffRow } from '../../../shared/services/audit-display.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { QuoteApiService } from '../../quotes/services/quote-api.service';
import { ProductoApiService } from '../../productos/services/producto-api.service';
import { MeetingApiService } from '../../meeting/services/meeting-api.service';
import { TaskApiService } from '../../tasks/services/task-api.service';
import { TicketsApiService } from '../../tickets/services/tickets-api.service';
import { SegmentService } from '../../segments/services/segment-api.service';
import { MarketingCampaignApiService } from '../../marketing-email/services/marketing-campaign-api.service';

/** Opción de filtro de operación: una etiqueta puede agrupar varios valores crudos (CREATE+INSERT). */
interface OperationOption {
  label: string;
  /** Valores crudos separados por coma (el backend solo acepta uno por request: se envía el primero). */
  value: string;
}

/** Opción normalizada del buscador-selector de entidad (id numérico + textos ya resueltos). */
interface EntityOption {
  id: number;
  label: string;
  sublabel: string;
}

/** Config de búsqueda por tipo de entidad: solo difiere la función `search`. */
interface EntitySearchConfig {
  search: (term: string, page: number) => Observable<EntitySelectPage<EntityOption>>;
}

@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, EntitySelectComponent],
  templateUrl: './audit-logs.component.html'
})
export class AuditLogsComponent implements OnInit {
  logs: AuditLogResponse[] = [];
  loading = false;
  error = '';

  // Pagination
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;

  // Filters
  filterEntityName = '';
  filterIdEntity = '';
  filterModuleName = '';
  filterOperationType = '';
  filterOperationSubtype = '';
  filterSeverity = '';
  filterSuccess: '' | 'true' | 'false' = '';
  filterIdUser: number | null = null;
  filterDateFrom = '';
  filterDateTo = '';
  showFilters = false;

  // ── Buscador-selector de entidad por nombre (reemplaza el input libre de "ID Entidad") ──
  /** Id elegido en el buscador (espejo numérico de filterIdEntity, que es el string que se envía al backend). */
  selectedEntityId: number | null = null;

  /** Adaptadores de presentación para EntitySelectComponent (cada item ya viene normalizado). */
  optionLabel = (o: EntityOption): string => o.label;
  optionSublabel = (o: EntityOption): string => o.sublabel;
  optionId = (o: EntityOption): number => o.id;

  // Detail
  selectedLog: AuditLogResponse | null = null;
  selectedLogDiffs: AuditDiffRow[] = [];
  expandedLogId: number | null = null;

  // User resolution for detail modal
  detailUserFullName = '';
  loadingDetailUser = false;

  // Catálogos
  users: DirectoryUser[] = [];
  loadingUsers = false;

  // Fallback hardcodeado si GET /catalog falla (rol insuficiente, error de red, etc.)
  entityOptions = [
    'contact', 'company', 'lead', 'deal',
    'quote', 'meeting', 'task', 'ticket',
    'product', 'price_list', 'product_bundle', 'discount_rule',
    'segment', 'campaign', 'template',
    'invitation', 'invitation_template', 'user'
  ];
  moduleOptions = [
    'crmcore', 'auth', 'communication', 'meeting',
    'product', 'quote', 'storage', 'invitation',
    'oauth', 'logs'
  ];
  // Se inicializa en ngOnInit (no como field initializer): buildOperationOptions() usa
  // this.display, que solo queda asignado una vez que corre el cuerpo del constructor.
  // Con useDefineForClassFields los field initializers corren ANTES que las propiedades
  // del constructor, así que aquí this.display aún sería undefined → TypeError y la
  // pantalla quedaría en blanco.
  operationOptions: OperationOption[] = [];
  /** Subtipos del catálogo; si está vacío, el filtro de subtipo queda como input libre. */
  subtypeOptions: string[] = [];
  severityOptions = ['INFO', 'WARN', 'ERROR'];

  constructor(
    private auditApi: AuditApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private userDirectory: UserDirectoryApiService,
    public display: AuditDisplayService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private leadApi: LeadApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private productoApi: ProductoApiService,
    private meetingApi: MeetingApiService,
    private taskApi: TaskApiService,
    private ticketsApi: TicketsApiService,
    private segmentApi: SegmentService,
    private campaignApi: MarketingCampaignApiService
  ) {}

  // ───────────────────── Buscador-selector de entidad ─────────────────────
  // Mapea cada entityName CRUDO del catálogo a su API de búsqueda por nombre.
  // Los entityName sin entrada (catálogos internos, mkt_form_*, ids string como
  // template) caen al input de id libre. Las arrow functions conservan `this`;
  // referencian los services del constructor pero solo se invocan al buscar (no
  // en la inicialización del campo), así que los services ya están asignados.
  private readonly entitySearch: Record<string, EntitySearchConfig> = {
    contact: {
      search: (term, page) => this.fromPage(
        this.contactoApi.searchContacts({ businessId: this.bid(), searchTerm: term, page, size: 10 }),
        (c) => ({ id: c.id, label: `${c.fullname ?? ''} ${c.lastname ?? ''}`.trim() || `Contacto ${c.id}`, sublabel: c.mail ?? c.phone ?? '' })
      )
    },
    company: {
      search: (term, page) => this.fromPage(
        this.empresaApi.searchCompanies({ businessId: this.bid(), searchTerm: term, page, size: 10 }),
        (c) => ({ id: c.id, label: c.commercialName ?? c.businessName ?? c.legalName ?? `Empresa ${c.id}`, sublabel: c.legalName ?? c.businessName ?? '' })
      )
    },
    lead: {
      search: (term, page) => this.fromPage(
        this.leadApi.searchLeads({ businessId: this.bid(), searchName: term, page, size: 10 }),
        (l) => ({ id: l.id, label: l.name ?? l.fullname ?? `Lead ${l.id}`, sublabel: l.mail ?? l.email ?? '' })
      )
    },
    deal: {
      search: (term, page) => this.fromPage(
        this.dealApi.searchDeals({ businessId: this.bid(), searchName: term, page, size: 10 }),
        (d) => ({ id: d.id, label: d.name ?? `Negocio ${d.id}`, sublabel: d.amount != null ? String(d.amount) : '' })
      )
    },
    quote: {
      search: (term, page) => this.fromPage(
        this.quoteApi.searchQuotes({ businessId: this.bid(), searchQuoteNumber: term, page, size: 10 }),
        (q) => ({ id: q.id, label: q.quoteNumber ?? `Cotización ${q.id}`, sublabel: q.statusName ?? q.dealName ?? '' })
      )
    },
    product: {
      search: (term, page) => this.fromPage(
        this.productoApi.searchProduct({ businessId: this.bid(), searchName: term, page, size: 10 }),
        (p) => ({ id: p.id, label: p.name ?? `Producto ${p.id}`, sublabel: p.sku ?? '' })
      )
    },
    meeting: {
      search: (term, page) => this.fromPage(
        this.meetingApi.searchMeetings({ businessId: this.bid(), searchTitle: term, page, size: 10 }),
        (m) => ({ id: m.id, label: m.title ?? `Reunión ${m.id}`, sublabel: m.datetimeStart ?? '' })
      )
    },
    task: {
      search: (term, page) => this.fromPage(
        this.taskApi.filterTasks({ searchTerm: term, page, size: 10 }),
        (t) => ({ id: t.id, label: t.title ?? `Tarea ${t.id}`, sublabel: t.datetimeDue ?? '' })
      )
    },
    ticket: {
      // /api/tickets/search?q= ya filtra server-side y devuelve un array plano.
      search: (term) => this.fromArray(
        this.ticketsApi.quickSearchTickets(term), term,
        (t) => ({ id: t.id, label: t.subject ?? t.title ?? `Ticket #${t.id}`, sublabel: t.ticketNumber ?? '' }),
        () => true
      )
    },
    user: {
      // Directorio de la empresa: filtra por texto SERVER-SIDE y solo exige estar autenticado
      // (antes usaba /api/auth/users/search, gateado con `users:VIEW`: para un rol sin ese
      // permiso el desplegable salía vacío). Por eso el filtro client-side es `() => true`.
      search: (term) => this.fromArray(
        this.userDirectory.searchUsers(term, 50),
        term,
        (u) => ({ id: u.id, label: directoryUserLabel(u), sublabel: u.mail ?? '' }),
        () => true
      )
    },
    segment: {
      search: (term, page) => this.fromPage(
        this.segmentApi.getSegments(page, 10, { searchTerm: term }),
        (s) => ({ id: s.id, label: s.name ?? `Segmento ${s.id}`, sublabel: s.description ?? s.type ?? '' })
      )
    },
    campaign: {
      // list() no acepta filtro de texto → filtrar client-side.
      search: (term) => this.fromArray(
        this.campaignApi.list(), term,
        (c) => ({ id: c.id, label: c.name ?? `Campaña ${c.id}`, sublabel: c.subject ?? c.status ?? '' }),
        (c, t) => (c.name ?? '').toLowerCase().includes(t)
      )
    }
  };

  /** Etiqueta amigable del tipo de entidad para el placeholder del buscador. */
  private readonly entityNoun: Record<string, string> = {
    contact: 'contacto', company: 'empresa', lead: 'lead', deal: 'negocio',
    quote: 'cotización (por número)', product: 'producto', meeting: 'reunión',
    task: 'tarea', ticket: 'ticket', user: 'usuario', segment: 'segmento', campaign: 'campaña'
  };

  private bid(): number {
    return this.authService.getCurrentUser()?.businessId ?? 1;
  }

  /** Adapta una respuesta paginada (Spring) a la página normalizada del selector. */
  private fromPage(
    src: Observable<{ content?: unknown[]; totalPages?: number }>,
    mapFn: (item: any) => EntityOption
  ): Observable<EntitySelectPage<EntityOption>> {
    return src.pipe(
      map(r => ({ items: (r.content ?? []).map(mapFn), totalPages: r.totalPages ?? 1 })),
      catchError(() => of({ items: [] as EntityOption[], totalPages: 0 }))
    );
  }

  /** Adapta un array plano (sin paginación) filtrando client-side por `term`. */
  private fromArray(
    src: Observable<unknown[]>,
    term: string,
    mapFn: (item: any) => EntityOption,
    matchFn: (item: any, t: string) => boolean
  ): Observable<EntitySelectPage<EntityOption>> {
    return src.pipe(
      map(arr => {
        const t = term.toLowerCase().trim();
        const items = (arr ?? []).filter(i => !t || matchFn(i, t)).map(mapFn);
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as EntityOption[], totalPages: 0 }))
    );
  }

  /** Config de búsqueda del tipo de entidad seleccionado, o undefined si no hay/no se soporta. */
  get activeEntitySearch(): EntitySearchConfig | undefined {
    return this.filterEntityName ? this.entitySearch[this.filterEntityName] : undefined;
  }

  /** Lista de 0 o 1 elemento; el @for usa `filterEntityName` como track para RECREAR el buscador al cambiar de tipo. */
  get activeEntityConfigList(): EntitySearchConfig[] {
    const cfg = this.activeEntitySearch;
    return cfg ? [cfg] : [];
  }

  get entitySelectPlaceholder(): string {
    const noun = this.filterEntityName ? this.entityNoun[this.filterEntityName] : undefined;
    return noun ? `Buscar ${noun} por nombre…` : 'Buscar por nombre…';
  }

  /** Al cambiar el TIPO de entidad: resetea el id elegido y recarga (el buscador se recrea por el track). */
  onEntityTypeChange(): void {
    this.selectedEntityId = null;
    this.filterIdEntity = '';
    this.loadLogs(0);
  }

  /** Al elegir (o limpiar) un registro en el buscador: fija `filterIdEntity` (string) y recarga. */
  onEntityPicked(id: number | null): void {
    this.selectedEntityId = id;
    this.filterIdEntity = id != null ? String(id) : '';
    this.loadLogs(0);
  }

  ngOnInit(): void {
    this.operationOptions = this.buildOperationOptions(['CREATE', 'INSERT', 'UPDATE', 'DELETE', 'READ']);
    this.loadCatalog();
    this.loadUsers();
    this.loadLogs();
  }

  /** Pobla los filtros con los valores reales del tenant (GET /api/audit/logs/catalog). */
  private loadCatalog(): void {
    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) return;
    this.auditApi.getCatalog(businessId).subscribe({
      next: catalog => {
        if (catalog.entities?.length) this.entityOptions = catalog.entities;
        if (catalog.modules?.length) this.moduleOptions = catalog.modules;
        if (catalog.operationTypes?.length) {
          this.operationOptions = this.buildOperationOptions(catalog.operationTypes);
        }
        this.subtypeOptions = catalog.operationSubtypes ?? [];
      },
      error: () => { /* se mantienen las opciones hardcodeadas */ }
    });
  }

  /** Agrupa CREATE e INSERT en una sola opción visual "Creación". */
  private buildOperationOptions(raw: string[]): OperationOption[] {
    const creation = raw.filter(o => o === 'CREATE' || o === 'INSERT');
    const others = raw.filter(o => o !== 'CREATE' && o !== 'INSERT');
    const options: OperationOption[] = [];
    if (creation.length > 0) {
      options.push({ label: 'Creación', value: creation.join(',') });
    }
    for (const op of others) {
      options.push({ label: this.display.translateOperation(op), value: op });
    }
    return options;
  }

  private loadUsers(): void {
    // El directorio saca el negocio del token: ya no hace falta resolver el businessId aquí.
    this.loadingUsers = true;
    this.userDirectory.searchUsers('', 200).subscribe({
      next: data => {
        this.users = data;
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
      }
    });
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.filterEntityName) count++;
    if (this.filterIdEntity) count++;
    if (this.filterModuleName) count++;
    if (this.filterOperationType) count++;
    if (this.filterOperationSubtype) count++;
    if (this.filterSeverity) count++;
    if (this.filterSuccess !== '') count++;
    if (this.filterIdUser != null) count++;
    if (this.filterDateFrom) count++;
    if (this.filterDateTo) count++;
    return count;
  }

  loadLogs(page = 0): void {
    this.loading = true;
    this.error = '';
    this.currentPage = page;

    const params: AuditLogSearchParams = {
      page,
      size: this.pageSize
    };
    if (this.filterEntityName) params.entityName = this.filterEntityName;
    if (this.filterIdEntity) params.idEntity = this.filterIdEntity.trim();
    if (this.filterModuleName) params.moduleName = this.filterModuleName;
    if (this.filterOperationType) {
      // El backend solo acepta un operationType por request: si la opción
      // agrupa CREATE+INSERT se envía el primero presente en el catálogo.
      params.operationType = this.filterOperationType.split(',')[0];
    }
    if (this.filterOperationSubtype) params.operationSubtype = this.filterOperationSubtype.trim();
    if (this.filterSeverity) params.severity = this.filterSeverity;
    if (this.filterSuccess !== '') params.success = this.filterSuccess === 'true';
    if (this.filterIdUser != null) params.idUser = this.filterIdUser;
    if (this.filterDateFrom) params.dateFrom = new Date(this.filterDateFrom).toISOString();
    if (this.filterDateTo) params.dateTo = new Date(this.filterDateTo).toISOString();

    this.auditApi.getLogs(params).subscribe({
      next: data => {
        this.logs = data.content ?? [];
        this.currentPage = data.page ?? page;
        this.totalElements = data.totalElements ?? 0;
        this.totalPages = data.totalPages ?? 1;
        this.loading = false;
      },
      error: () => {
        this.error = 'Error al cargar los registros de auditoría.';
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.loadLogs(0);
  }

  clearFilters(): void {
    this.filterEntityName = '';
    this.filterIdEntity = '';
    this.selectedEntityId = null;
    this.filterModuleName = '';
    this.filterOperationType = '';
    this.filterOperationSubtype = '';
    this.filterSeverity = '';
    this.filterSuccess = '';
    this.filterIdUser = null;
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.loadLogs(0);
  }

  userLabel(u: DirectoryUser): string {
    const name = [u.fullname, u.lastname].filter(Boolean).join(' ').trim();
    return name && u.mail ? `${name} (${u.mail})` : directoryUserLabel(u);
  }

  openDetail(log: AuditLogResponse): void {
    this.selectedLog = log;
    this.selectedLogDiffs = this.display.getDiffRows(log);
    this.resolveDetailUser(log);
  }

  closeDetail(): void {
    this.selectedLog = null;
    this.selectedLogDiffs = [];
    this.detailUserFullName = '';
  }

  private resolveDetailUser(log: AuditLogResponse): void {
    // If the API already gives us a full name, use it
    if (log.userFullName) {
      this.detailUserFullName = log.userFullName;
      return;
    }

    // Check if it's the current logged-in user (no API call needed)
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && log.idUser === currentUser.id) {
      this.detailUserFullName = [currentUser.fullname, currentUser.lastname].filter(Boolean).join(' ');
      return;
    }

    // Otherwise fetch from API
    if (log.idUser) {
      this.loadingDetailUser = true;
      this.detailUserFullName = log.username ?? '';
      this.authApiService.getUserById(log.idUser).subscribe({
        next: user => {
          this.detailUserFullName = [user.fullname, user.lastname].filter(Boolean).join(' ');
          this.loadingDetailUser = false;
        },
        error: () => {
          // El directorio devuelve también a los inactivos, así que llegar aquí significa
          // usuario eliminado: se dice, en vez de enseñar el id.
          this.detailUserFullName = log.username ?? 'Usuario no disponible';
          this.loadingDetailUser = false;
        }
      });
    } else {
      this.detailUserFullName = log.username ?? '—';
    }
  }

  toggleLogDetail(logId: number): void {
    this.expandedLogId = this.expandedLogId === logId ? null : logId;
  }
}
